/**
 * R232q5i: KayKit Adventurer character pipeline УДАЛЁН.
 *
 * Юзер потребовал «из старого вообще ничего не нужно — только Sirus».
 * Все KayKit Adventurer GLBs / animation packs / weapon meshes больше не
 * грузятся. Вместо этого этот модуль теперь — тонкая обёртка над
 * RealisticCharacter, который сам грузит Sirus extracted character
 * GLBs из /aiwari/models/characters/.
 *
 * Public API сохранён (loadKayKitCharacter, KayKitRig, loadAllRigMediumAnimations)
 * чтобы World.tsx не требовал хирургии: новые callsites должны переходить
 * на loadRealisticCharacter напрямую, но старый код продолжает работать.
 */

import {
  TransformNode,
  Vector3,
  type AnimationGroup,
  type Scene,
  type Skeleton,
} from '@babylonjs/core';
import type { Slot } from '../data/equipment.js';
import { getRaceModelGlb, AIWARI_CHAR_BASE, AIWARI_CHAR_PLACEHOLDER, type Race, type Gender } from './RaceModelMap.js';
import { loadRealisticCharacter, type RealisticRig } from './RealisticCharacter.js';
import { attachWeaponToBone, detachWeapon, type AttachableRig } from './ItemMeshAttach.js';

/** Заглушка для KayKitMob.ts — мы больше не грузим Rig_Medium анимации
 *  из KayKit pack, так что возвращаем пустую map. KayKitMob shim это
 *  теперь не использует, но оставляем экспорт чтобы старый код собирался. */
export async function loadAllRigMediumAnimations(_scene: Scene): Promise<Map<string, AnimationGroup>> {
  return new Map();
}

export interface KayKitRig {
  root: TransformNode;
  setColours: () => void;
  setMoving: (moving: boolean, running?: boolean) => void;
  update: (dt: number) => void;
  setEquipment: (equipped: Partial<Record<Slot, string>>) => void;
  setCosmetics: (eq: { cape: string | null; pet: string | null }) => void;
  triggerAttack: () => void;
  /** WotLK 3.3.5a emote (dance/wave/sit/etc). */
  triggerEmote?: (name: string) => void;
  /** R232q5b1 (Sirus visuals): play a named cast/spell animation once. */
  triggerAnim?: (name: string) => boolean;
  /** pause/resume all anim groups для distance-cull. */
  setAnimEnabled?: (en: boolean) => void;
  dispose: () => void;
}

/**
 * R232q5i: загружает Sirus AIWARI character через RealisticCharacter
 * loader. Резолвит race+gender → /aiwari/models/characters/aiwari_*.glb.
 * Если primary GLB ещё не извлечён — fallback на _loading_placeholder.glb.
 */
/**
 * R232q86i: REAL KayKit Adventurer loader. Bypasses Sirus M2→GLB pipeline
 * (which produces broken bind poses → torn «ошмётки» mesh fragments). Loads
 * one of 6 KayKit Adventurer GLBs based on classId; ignores race+gender for
 * now since KayKit set has only 6 silhouettes total. Returns a properly
 * skinned rig with working animations.
 *
 * Class → KayKit silhouette mapping:
 *   warrior, deathKnight, paladin → Knight.glb
 *   hunter                        → Ranger.glb
 *   rogue                         → Rogue.glb (or Rogue_Hooded for DK)
 *   priest, mage, warlock         → Mage.glb
 *   shaman, druid                 → Barbarian.glb (closest robe-less rig)
 */
const KAYKIT_BASE = `${import.meta.env.BASE_URL}models/kaykit/adventurers/Characters/gltf/`;
function pickKaykitFile(classId: string | undefined): string {
  switch (classId) {
    case 'rogue':       return 'Rogue.glb';
    case 'deathKnight': return 'Rogue_Hooded.glb';
    case 'hunter':
    case 'ranger':      return 'Ranger.glb';
    case 'mage':
    case 'warlock':
    case 'priest':
    case 'cleric':      return 'Mage.glb';
    case 'shaman':
    case 'druid':
    case 'barbarian':   return 'Barbarian.glb';
    default:            return 'Knight.glb';
  }
}

async function loadKayKitDirect(scene: Scene, classId: string | undefined): Promise<KayKitRig | null> {
  const file = pickKaykitFile(classId);
  const url = KAYKIT_BASE + file;
  try {
    const probe = await fetch(url, { method: 'HEAD' });
    if (!probe.ok) return null;
    const ct = (probe.headers.get('content-type') ?? '').toLowerCase();
    if (ct.includes('text/html')) return null;
    const { LoadAssetContainerAsync } = await import('@babylonjs/core/Loading/sceneLoader.js');
    const container = await LoadAssetContainerAsync(url, scene, { pluginExtension: '.glb' });
    const inst = container.instantiateModelsToScene((n) => n, true);
    const root = new TransformNode(`kaykit-${file}-root`, scene);
    // Move all root nodes from the instance under our root.
    for (const n of inst.rootNodes) {
      n.parent = root;
    }
    // R232q86i: KayKit Mage native bbox is ~2.65m (cape extends below feet);
    // Knight/Ranger/Rogue ~2m. Normalize ALL to ~1.85m WoW-human height
    // by measuring after instantiation and scaling proportionally.
    {
      let minY = Infinity, maxY = -Infinity;
      for (const n of inst.rootNodes) {
        const meshes = (n as { getChildMeshes?: (d?: boolean) => Array<{ getBoundingInfo?: () => { boundingBox: { minimumWorld: { y: number }; maximumWorld: { y: number } } } }> }).getChildMeshes?.(false) ?? [];
        for (const m of meshes) {
          if (typeof m.getBoundingInfo !== 'function') continue;
          const bb = m.getBoundingInfo().boundingBox;
          if (bb.minimumWorld.y < minY) minY = bb.minimumWorld.y;
          if (bb.maximumWorld.y > maxY) maxY = bb.maximumWorld.y;
        }
      }
      const nativeH = (maxY > minY && Number.isFinite(minY) && Number.isFinite(maxY)) ? (maxY - minY) : 1;
      const targetH = 1.85;
      const s = targetH / nativeH;
      root.scaling = new Vector3(s, s, s);
    }
    root.rotation.y = Math.PI; // face camera
    // Start the first animation group (idle) if available.
    const idle = inst.animationGroups.find((g) => /idle|stand/i.test(g.name)) ?? inst.animationGroups[0];
    if (idle) idle.play(true);
    // R232q86zb9: Pick up the GLB skeleton so we can attach weapon meshes
    // to the right_hand / left_hand bones. KayKit Adventurer rigs ship a
    // single skeleton with handslot.l/handslot.r bones — these are matched
    // by ItemMeshAttach.findBone via the standard pattern list.
    const skeleton: Skeleton | null = inst.skeletons[0] ?? null;
    // Stable rig identity for ItemMeshAttach's WeakMap-keyed attach state.
    const rigShim: AttachableRig = { root, skeleton, inner: root };
    // Track current equipped state so cosmetic re-renders can diff slots.
    let currentEquipped: Partial<Record<Slot, string>> = {};
    const rig: KayKitRig = {
      root,
      setColours: () => {},
      setMoving: (moving: boolean, _running?: boolean) => {
        const wantedAnim = moving
          ? inst.animationGroups.find((g) => /walk|run/i.test(g.name))
          : inst.animationGroups.find((g) => /idle|stand/i.test(g.name));
        if (wantedAnim && !wantedAnim.isStarted) {
          for (const g of inst.animationGroups) g.stop();
          wantedAnim.play(true);
        }
      },
      update: () => {},
      // R232q86zb9: real equipment attach via ItemMeshAttach. Previously a
      // no-op, which silently dropped every weapon on every KayKit-loaded
      // character (i.e. all classes after R232q86i — KayKit succeeds for all
      // classIds). attachWeaponToBone HEAD-probes its candidate URLs, master-
      // caches the parsed AssetContainer, and falls back to a generic CC0
      // sword if the Sirus weapon GLB is missing — so any equipped item now
      // produces a visible mesh on the appropriate hand bone.
      setEquipment: (equipped) => {
        const mh = equipped.mainhand ?? '';
        const oh = equipped.offhand ?? '';
        // mainhand
        if (mh !== (currentEquipped.mainhand ?? '')) {
          if (mh) {
            void attachWeaponToBone(rigShim, 'mainhand', mh).catch(() => {});
          } else {
            detachWeapon(rigShim, 'mainhand');
          }
        }
        // offhand
        if (oh !== (currentEquipped.offhand ?? '')) {
          if (oh) {
            void attachWeaponToBone(rigShim, 'offhand', oh).catch(() => {});
          } else {
            detachWeapon(rigShim, 'offhand');
          }
        }
        currentEquipped = { ...equipped };
      },
      setCosmetics: () => {},
      triggerAttack: () => {
        const attack = inst.animationGroups.find((g) => /attack|swing/i.test(g.name));
        if (attack) {
          attack.play(false);
          attack.onAnimationEndObservable.addOnce(() => {
            const idle2 = inst.animationGroups.find((g) => /idle|stand/i.test(g.name));
            if (idle2) idle2.play(true);
          });
        }
      },
      triggerEmote: () => {},
      triggerAnim: () => false,
      setAnimEnabled: (en: boolean) => {
        for (const g of inst.animationGroups) g.speedRatio = en ? 1 : 0;
      },
      dispose: () => {
        // R232q86zb9: detach any weapons we attached so their TransformNodes
        // and the per-rig slot map entry are released before the rig root
        // dies (otherwise next-load re-attach would try to dispose a stale
        // disposed node from the WeakMap).
        try { detachWeapon(rigShim, 'mainhand'); } catch { /* nop */ }
        try { detachWeapon(rigShim, 'offhand'); } catch { /* nop */ }
        for (const g of inst.animationGroups) g.stop();
        inst.dispose();
        root.dispose();
      },
    };
    return rig;
  } catch (e) {
    console.warn(`[KayKit] direct load failed for ${url}`, e);
    return null;
  }
}

export async function loadKayKitCharacter(scene: Scene, opts?: {
  classId?: string;
  gender?: Gender;
  scale?: number;
  raceId?: string;
}): Promise<KayKitRig> {
  // R232q86i: try real KayKit GLB first — it has clean skeleton + animations.
  // If KayKit fails, fall through to Sirus chain (which is broken now but
  // may be re-extracted properly later).
  const direct = await loadKayKitDirect(scene, opts?.classId);
  if (direct) return direct;
  const race = ((opts?.raceId ?? 'human') as Race);
  const gender = opts?.gender ?? 'male';
  const pick = getRaceModelGlb(race, gender);
  // R232q5b0: iterate Sirus-only chain. Each step is HEAD-probed (with
  // strict content-type check — SPA fallback HTML must NOT be accepted)
  // and the procedural sentinel is matched WITHOUT a network probe.
  let rig: RealisticRig | null = null;
  for (const step of pick.chain) {
    if (step.glb === AIWARI_CHAR_PLACEHOLDER) {
      // Procedural sentinel — load directly (builds neutral grey
      // "Loading Sirus rig..." body inside RealisticCharacter).
      rig = await loadRealisticCharacter(scene, {
        glb: AIWARI_CHAR_PLACEHOLDER,
        baseDir: AIWARI_CHAR_BASE,
        scale: pick.scale * (opts?.scale ?? 1.0),
        targetTallest: pick.targetHeight,
        raceId: race,
        gender,
      });
      break;
    }
    try {
      const probe = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
      if (!probe.ok) continue;
      // SPA fallback HTML guard — nginx serves index.html with 200 for
      // missing files. Only proceed if content-type is binary GLB.
      const ct = (probe.headers.get('content-type') ?? '').toLowerCase();
      if (ct.includes('text/html')) continue;
      rig = await loadRealisticCharacter(scene, {
        glb: step.glb,
        baseDir: step.baseDir,
        scale: pick.scale * (opts?.scale ?? 1.0),
        targetTallest: pick.targetHeight,
        raceId: race,
        gender,
      });
      break;
    } catch (e) {
      // Per user mandate: NEVER swallow Sirus parse errors — surface them.
      console.error(`[loadKayKitCharacter] chain step failed for ${step.baseDir}${step.glb}`, e);
    }
  }
  if (!rig) {
    // Last resort — direct procedural placeholder. Should be unreachable
    // because the chain already terminates with the sentinel, but kept as
    // a defensive net so the function never returns null.
    rig = await loadRealisticCharacter(scene, {
      glb: AIWARI_CHAR_PLACEHOLDER,
      baseDir: AIWARI_CHAR_BASE,
      scale: pick.scale * (opts?.scale ?? 1.0),
      targetTallest: pick.targetHeight,
      raceId: race,
      gender,
    });
  }
  // RealisticRig уже совместим с KayKitRig по shape — возвращаем как есть.
  // playEmote → triggerEmote alias для compat с старыми вызывающими.
  const wrapped: KayKitRig = {
    root: rig.root,
    setColours: rig.setColours,
    setMoving: rig.setMoving,
    update: rig.update,
    setEquipment: rig.setEquipment,
    setCosmetics: rig.setCosmetics,
    triggerAttack: rig.triggerAttack,
    triggerEmote: rig.playEmote,
    triggerAnim: rig.triggerAnim,
    dispose: rig.dispose,
  };
  return wrapped;
}
